import { useDraggable } from '@dnd-kit/core';
import { getCardImageUri } from '@mtg-deck-tool/shared';
import type { CardInstance } from '@mtg-deck-tool/shared';
import { usePlaytesterStore } from '../../stores/playtester-store.js';
import CardBadges from './CardBadges.js';
import { CARD_CORNER_RADIUS } from '../../lib/playtester-engine.js';

interface BattlefieldCardProps {
  instance: CardInstance;
}

export default function BattlefieldCard({ instance }: BattlefieldCardProps) {
  const { instanceId, card, tapped, faceDown, doesntUntap, castCount, x, y } = instance;

  const selectedInstanceIds = usePlaytesterStore((s) => s.selectedInstanceIds);
  const isClickToTapMode = usePlaytesterStore((s) => s.isClickToTapMode);
  const tapCard = usePlaytesterStore((s) => s.tapCard);
  const toggleSelection = usePlaytesterStore((s) => s.toggleSelection);
  const setSelection = usePlaytesterStore((s) => s.setSelection);
  const setContextMenu = usePlaytesterStore((s) => s.setContextMenu);
  const setHoveredCard = usePlaytesterStore((s) => s.setHoveredCard);
  const setHoveredInstanceId = usePlaytesterStore((s) => s.setHoveredInstanceId);

  const isSelected = selectedInstanceIds.has(instanceId);
  // Use 'large' (672×936) for crisp rendering at high zoom; falls back to 'normal'.
  const artUri = !faceDown ? getCardImageUri(card, 'large') : undefined;

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: instanceId,
    data: { instanceId, zone: 'battlefield' },
  });

  function handleClick(e: React.MouseEvent) {
    if (isClickToTapMode) {
      tapCard(instanceId);
    } else if (e.shiftKey) {
      toggleSelection(instanceId);
    } else {
      setSelection([instanceId]);
    }
  }

  function handleContextMenu(e: React.MouseEvent) {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, instanceId, zone: 'battlefield' });
  }

  function handleMouseEnter() {
    setHoveredCard(card);
    setHoveredInstanceId(instanceId);
  }

  function handleMouseLeave() {
    setHoveredCard(null);
    setHoveredInstanceId(null);
  }

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`cursor-pointer select-none transition-transform duration-150 origin-center ${
        tapped ? 'rotate-90' : ''
      } ${isDragging ? 'opacity-40' : ''} ${x !== undefined ? 'absolute' : 'relative shrink-0'}`}
      style={{
        width: 'var(--pt-card-w)',
        height: 'var(--pt-card-h)',
        ...(x !== undefined ? { left: x, top: y } : {}),
      }}
    >
      {/* Card face */}
      <div
        className={`w-full h-full rounded-sm overflow-hidden border ${
          isSelected ? 'border-orange-500 ring-2 ring-orange-500' : 'border-gray-700'
        }`}
      >
        {artUri ? (
          <img src={artUri} alt={card.name} className="w-full h-full object-cover" draggable={false} style={{ borderRadius: CARD_CORNER_RADIUS }} />
        ) : faceDown ? (
          <div className="w-full h-full bg-blue-950 flex items-center justify-center">
            <div className="w-3/4 h-3/4 border-2 border-blue-700 rounded-sm" />
          </div>
        ) : (
          <div className="w-full h-full bg-gray-800 flex items-center justify-center p-0.5">
            <span className="text-gray-400 text-[8px] text-center leading-tight">{card.name}</span>
          </div>
        )}
      </div>

      {/* Counter and P/T badges */}
      <CardBadges instance={instance} />

      {/* doesntUntap padlock indicator */}
      {doesntUntap && (
        <div className="absolute top-0.5 right-0.5 bg-black/70 rounded-full w-3.5 h-3.5 flex items-center justify-center" title="Doesn't untap">
          <svg className="w-2.5 h-2.5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 2a4 4 0 00-4 4v2H4a2 2 0 00-2 2v6a2 2 0 002 2h12a2 2 0 002-2v-6a2 2 0 00-2-2h-2V6a4 4 0 00-4-4zm0 2a2 2 0 012 2v2H8V6a2 2 0 012-2zm0 8a2 2 0 100-4 2 2 0 000 4z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      )}

      {/* Commander cast count badge */}
      {castCount > 0 && (
        <div className="absolute top-0.5 left-0.5 bg-orange-600/90 text-white text-[7px] font-bold px-1 py-0.5 rounded leading-none">
          {castCount}×
        </div>
      )}
    </div>
  );
}
